import { useCallback, useEffect, useState } from "react";

import { MEMBERSHIP_EVENTS } from "src/components/Person/events";
import { useOrgDesignBetaEnabled } from "src/components/OrgChart/controller/dnd/featureFlags";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useOrgDesignConfig } from "src/insights/useOrgDesignConfig";
import eventEmitter, { EVENTS } from "src/services/eventEmitter";
import {
  INSIGHTS_HIERARCHY_VIEW,
  resolveDefaultInsightsHierarchyView,
} from "src/util/insightsHierarchyView";

export const INSIGHTS_STRUCTURE_EVENTS = [
  EVENTS.TEAM_OWNER_CHANGED,
  MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED,
  EVENTS.NEW_GROUP_ADDED,
  EVENTS.GROUP_REMOVED,
  EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED,
];

export const useInsightsHierarchyView = () => {
  const workspace = useWorkspace();
  const workspaceId = workspace?.id;
  const odBetaEnabled = useOrgDesignBetaEnabled();
  const {
    ready,
    loading: orgDesignLoading,
    allowLensSwitching,
    defaultViewRaw: workspaceDefaultView,
  } = useOrgDesignConfig();

  // Reporting-line lens is OD beta (release + Enable OD beta). Release OD locks
  // to the supply / team-owners lens even if workspace config still has
  // allowLensSwitching from a prior beta session.
  const lensSwitchingEnabled = Boolean(allowLensSwitching && odBetaEnabled);

  // null until workspace default is known — avoids a wrong first-paint query.
  const [hierarchyView, setHierarchyViewState] = useState<string | null>(null);
  const [userSetHierarchyView, setUserSetHierarchyView] = useState(false);
  const [hierarchyWorkspaceId, setHierarchyWorkspaceId] = useState(workspaceId);

  const setHierarchyView = useCallback((view: string) => {
    setUserSetHierarchyView(true);
    setHierarchyViewState(view);
  }, []);

  const defaultViewRaw =
    typeof workspaceDefaultView === "string" ||
    workspaceDefaultView === null ||
    workspaceDefaultView === undefined
      ? workspaceDefaultView
      : undefined;

  let userHasChosenView = userSetHierarchyView;
  if (hierarchyWorkspaceId !== workspaceId) {
    setHierarchyWorkspaceId(workspaceId);
    userHasChosenView = false;
    setUserSetHierarchyView(false);
    setHierarchyViewState(null);
  }
  if (!userHasChosenView && (ready || !orgDesignLoading)) {
    const resolved = resolveDefaultInsightsHierarchyView(defaultViewRaw);
    if (hierarchyView !== resolved) {
      setHierarchyViewState(resolved);
    }
  }

  useEffect(() => {
    const onConfigChanged = (payload?: {
      defaultInsightsHierarchyView?: string | null;
    }) => {
      setUserSetHierarchyView(false);
      setHierarchyViewState(
        resolveDefaultInsightsHierarchyView(
          payload?.defaultInsightsHierarchyView ?? defaultViewRaw
        )
      );
    };
    eventEmitter.on(EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED, onConfigChanged);
    return () => {
      eventEmitter.off(
        EVENTS.INSIGHTS_WORKSPACE_CONFIG_CHANGED,
        onConfigChanged
      );
    };
  }, [defaultViewRaw]);

  const effectiveHierarchyView = !lensSwitchingEnabled
    ? INSIGHTS_HIERARCHY_VIEW.SUPPLY
    : hierarchyView;

  return {
    hierarchyView: effectiveHierarchyView,
    setHierarchyView,
    lensSwitchingEnabled,
    hierarchyReady: Boolean(effectiveHierarchyView),
    orgDesignLoading,
    workspaceDefaultView: defaultViewRaw,
  };
};
